// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * I-9 Compliance Dashboard
 *
 * One tab per status; each tab queries only its own status via the GSI2 index,
 * paged 50 at a time. The GSI walks forward via cursors (no random offset), so page
 * numbers are collected as they're visited and total pages come from status-counts.
 * A separate tracker card follows records rerun from the UI.
 */
import { useState, useEffect, useRef } from 'react'
import type { FC } from 'react'
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { Users, RefreshCw, FileStack, Eye, CheckCircle, RotateCcw, Loader2, ChevronLeft, ChevronRight, MessageSquare, History, Search, X, Scissors, Trash2, Undo2, ArrowUp, ArrowDown, Calculator, Upload } from 'lucide-react'
import type { NodeUserViewProps } from '@/core/types'
import { useServices } from '@/app-shell/services'
import { useAuth } from '@/subsystems/workspaces'
import { PurgeableBadge, EmployeeNameBadge, type I9MatchResult } from './MatchBadges'
import { StatusBadge, OperationalWarning, statusColor, type OperationalError } from './StatusBadge'
import { DocsDialog } from './DocsDialog'
import { RetryDialog, type CropValues } from './RetryDialog'
import { RetryAllDialog } from './RetryAllDialog'
import { RematchAllDialog } from './RematchAllDialog'
import { RewriteOptixAllDialog } from './RewriteOptixAllDialog'
import { RewriteOptixBatchDialog } from './RewriteOptixBatchDialog'
import { CommentDialog } from './CommentDialog'
import { HistoryDialog } from './HistoryDialog'
import { PurgeDialog } from './PurgeDialog'
import { ShredDialog } from './ShredDialog'
import { formatTimestamp } from './format'
import { FailTypeSelect, type FailType } from './FailTypeSelect'
import { OptixDebugCard } from './OptixDebugCard'
import { MatchDebugCard } from './MatchDebugCard'
import { MatchRulesInfo } from './MatchRulesInfo'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface I9RunHistoryEntry {
  processedAt: string | null
  status: string | null
  error: string | null
  empName: string | null
  uid: number | null
  matchScore: number
  ssnMatch: boolean
  dobMatch: boolean
  firstNameMatch: boolean
  lastNameMatch: boolean
  comment: string | null
  failType: FailType | null
  cropTopFraction: number | null
  cropBottomFraction: number | null
}

interface OptixDocument {
  docId: string
  docNumber: number
  empName: string | null
  scanDate: string | null
  status: string
  processedAt: string | null
  matchResult?: I9MatchResult | null
  comment?: string | null
  failType?: FailType | null
  cropTopFraction?: number | null
  cropBottomFraction?: number | null
  optixWriteStatus?: string | null
  optixDeleteStatus?: string | null
  optixDeleteError?: string | null
  operationalError?: OperationalError | null
  history?: I9RunHistoryEntry[]
}

interface OptixDocumentsResponse {
  documents: OptixDocument[]
  totalCount: number
  nextCursor: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Status config
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { value: 'failed',        label: 'Error' },
  { value: 'matched',       label: 'Fully Matched' },
  { value: 'partial_match', label: 'Partial Match' },
  { value: 'no_match',      label: 'No Match' },
  { value: 'new',           label: 'New' },
  { value: 'waiting',       label: 'Waiting' },
  { value: 'shredded',      label: 'Shredded' },
  { value: 'purged',        label: 'Purged' },
  { value: 'all',           label: 'All' },
] as const

type TabValue = (typeof TABS)[number]['value']

// The disposition tabs whose matching rules the info popover explains.
const MATCH_RULE_TABS = ['matched', 'partial_match', 'no_match'] as const
const isMatchRuleTab = (v: TabValue): v is (typeof MATCH_RULE_TABS)[number] =>
  (MATCH_RULE_TABS as readonly string[]).includes(v)

// Operational tabs (errors and in-flight work) are visible to global admins only;
// everyone else sees just the reviewable/disposition buckets.
const ADMIN_ONLY_TABS: TabValue[] = ['failed', 'new', 'waiting']

// Statuses that are still in flight — no retry. "new" has its own tab; pending and
// awaiting_retry live under the Waiting tab.
const IN_FLIGHT_STATUSES = ['new', 'pending', 'awaiting_retry']

const PAGE_SIZE = 50

// Statuses that are terminal, reviewable outcomes — the ones that carry a reviewer fail type.
const FAIL_TYPE_STATUSES = ['failed', 'no_match', 'partial_match']

// Statuses where processing is done — no longer worth polling a rerun record
const TERMINAL_STATUSES = ['matched', 'partial_match', 'no_match', 'failed']

// Manual disposition buckets set by HR; documents here are never reprocessed. Only
// purgeable terminal-status documents can be shredded, and only shredded ones can be
// purged (mirrors the backend guards).
const MANUAL_STATUSES = ['shredded', 'purged']

// Tabs where the "Purgeable only" filter isn't offered.
const NO_PURGEABLE_FILTER_TABS: TabValue[] = ['no_match', 'shredded', 'purged']

// Purgeable = the retention date exists and is before today, evaluated at render time
// (the stored eligibleForDeletion flag goes stale as dates pass). Mirrors the backend's
// purgeable filter and shred guard.
function isPurgeable(doc: OptixDocument): boolean {
  const retentionExpires = doc.matchResult?.retentionExpires
  return Boolean(retentionExpires && retentionExpires < new Date().toISOString().slice(0, 10))
}

// The crop band the current run actually used; null fractions mean the host default band.
function formatCrop(top: number | null | undefined, bottom: number | null | undefined): string {
  return top != null && bottom != null ? `${top}–${bottom}` : 'default'
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export const I9UserView: FC<NodeUserViewProps> = ({ orgId, workspaceId }) => {
  const services = useServices()
  const apiClient = services.getClient()
  const queryClient = useQueryClient()
  const { isGlobalAdmin, isWorkspaceAdmin } = useAuth()

  // Global admins can retry; both global and workspace admins can review (comment / fail type).
  const canReview = isGlobalAdmin || isWorkspaceAdmin

  const [selectedTab, setSelectedTab] = useState<TabValue>('failed')

  // Non-admins never see the admin-only tabs, so the selection (including the 'failed'
  // default) is clamped to a visible tab rather than trusting the raw state.
  const visibleTabs = isGlobalAdmin ? TABS : TABS.filter((t) => !ADMIN_ONLY_TABS.includes(t.value))
  const activeTab = visibleTabs.some((t) => t.value === selectedTab) ? selectedTab : 'matched'

  // "Unreviewed only" hides documents a reviewer has already categorized (fail type set),
  // so reviewers working the queue in parallel don't redo each other's records. It only
  // means anything on the fail-type tabs, so the effective filter is gated by showFailType.
  const [unreviewedOnly, setUnreviewedOnly] = useState(false)
  const tabShowFailType = FAIL_TYPE_STATUSES.includes(activeTab)
  const filterUnreviewed = unreviewedOnly && tabShowFailType

  // "Purgeable only" keeps documents whose retention window has passed (filtered
  // server-side against today's date). Not offered on every tab, so the effective
  // filter is gated by the tab. Sort order follows the Doc # column header.
  const [purgeableOnly, setPurgeableOnly] = useState(false)
  const tabShowPurgeable = !NO_PURGEABLE_FILTER_TABS.includes(activeTab)
  const filterPurgeable = purgeableOnly && tabShowPurgeable
  const [sortAsc, setSortAsc] = useState(false)
  // Filtered result sets make the unfiltered tab counts meaningless for paging math.
  const isFiltered = filterUnreviewed || filterPurgeable

  // Cursor-based paging: cursors[i] is the start cursor for page i (cursors[0] === '').
  // Each fetched page appends the next page's cursor, so visited pages stay navigable.
  const [page, setPage] = useState(0)
  const [cursors, setCursors] = useState<string[]>([''])

  // Records rerun from the UI, tracked in the card below with their live status.
  const [rerunIds, setRerunIds] = useState<Set<string>>(new Set())
  const [rerunStatus, setRerunStatus] = useState<Record<string, OptixDocument>>({})

  // Admin bulk-retry selection and dialog.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [retryDialogOpen, setRetryDialogOpen] = useState(false)
  const [retryAllDialogOpen, setRetryAllDialogOpen] = useState(false)
  const [rematchAllDialogOpen, setRematchAllDialogOpen] = useState(false)
  const [rewriteOptixAllDialogOpen, setRewriteOptixAllDialogOpen] = useState(false)
  const [rewriteOptixBatchDialogOpen, setRewriteOptixBatchDialogOpen] = useState(false)

  // Per-row comment editing (independent of selection; doesn't change status).
  const [commentTarget, setCommentTarget] = useState<OptixDocument | null>(null)

  // Per-row run-history viewing (read-only).
  const [historyTarget, setHistoryTarget] = useState<OptixDocument | null>(null)

  // Purge confirmation (shredded documents only).
  const [purgeTarget, setPurgeTarget] = useState<OptixDocument | null>(null)

  // Force-shred confirmation (global admin only): shred a document from any processing
  // bucket, bypassing the terminal-status and retention guards. Requires typing the id.
  const [forceShredTarget, setForceShredTarget] = useState<OptixDocument | null>(null)

  // Document search. searchInput is the live field; searchId is the submitted term that
  // drives the lookup. searchBy names which identifier the term is — the Optix doc number
  // or the record object id — since the two share a numeric range and can't be told apart
  // by value. When searchId is set, the table shows the single matched document across any
  // tab or status instead of the paged list.
  const [searchBy, setSearchBy] = useState<'doc_number' | 'object_id'>('doc_number')
  const [searchInput, setSearchInput] = useState('')
  const [searchId, setSearchId] = useState<string | null>(null)
  const isSearching = searchId !== null

  const basePath = `/org/${orgId}/ws/${workspaceId}/i9`

  const {
    data,
    isLoading,
    isFetching,
    error: fetchError,
    refetch,
  } = useQuery({
    queryKey: ['i9', 'optix-documents', orgId, workspaceId, activeTab, page, filterUnreviewed, filterPurgeable, sortAsc],
    enabled: !isSearching,
    placeholderData: keepPreviousData,
    // Poll only while filtering to unreviewed, so reviewers see records drop off the queue
    // as coworkers categorize them. Off by default — no recurring row fetch otherwise.
    refetchInterval: filterUnreviewed ? 15000 : false,
    queryFn: async (): Promise<OptixDocumentsResponse> => {
      const startCursor = cursors[page]
      const cursor = startCursor ? `&cursor=${encodeURIComponent(startCursor)}` : ''
      const unreviewed = filterUnreviewed ? '&unreviewedOnly=true' : ''
      const purgeable = filterPurgeable ? '&purgeableOnly=true' : ''
      const sort = sortAsc ? '&sort=asc' : ''
      const response = await apiClient.get<OptixDocumentsResponse>(
        `${basePath}/optix-documents?tab=${activeTab}&limit=${PAGE_SIZE}${cursor}${unreviewed}${purgeable}${sort}`
      )
      return response.data
    },
  })

  // Lookup a single document across any tab/status by the chosen identifier. Enabled only
  // when a search term is submitted; a missing id 404s, surfaced as "not found" below.
  const {
    data: searchResult,
    isFetching: searchFetching,
  } = useQuery({
    queryKey: ['i9', 'document-search', orgId, workspaceId, searchBy, searchId],
    enabled: isSearching,
    retry: false,
    queryFn: async (): Promise<OptixDocument> => {
      const response = await apiClient.get<OptixDocument>(
        `${basePath}/documents/${encodeURIComponent(searchId ?? '')}?by=${searchBy}`
      )
      return response.data
    },
  })

  // While searching, the table shows just the matched document (or nothing) and paging is
  // hidden; otherwise it shows the paged list for the active tab.
  const documents = isSearching ? (searchResult ? [searchResult] : []) : (data?.documents ?? [])
  const optixTotalCount = data?.totalCount ?? null

  // The fail-type column follows the tab normally, but a searched document can be any
  // status, so derive it from that document instead.
  const showFailType = isSearching
    ? searchResult !== undefined && FAIL_TYPE_STATUSES.includes(searchResult.status)
    : tabShowFailType

  // Record the next page's start cursor once we've fetched a page, so already-walked
  // pages remain directly clickable.
  useEffect(() => {
    const nextCursor = data?.nextCursor
    if (!nextCursor) return
    setCursors((prev) => {
      if (prev[page + 1] === nextCursor) return prev
      const next = [...prev]
      next[page + 1] = nextCursor
      return next
    })
  }, [data, page])

  // Per-tab document counts, kept fresh on a slow poll and after each transition.
  const { data: tabCounts, refetch: refetchCounts } = useQuery({
    queryKey: ['i9', 'status-counts', orgId, workspaceId],
    refetchInterval: 10000,
    queryFn: async (): Promise<Record<string, number>> => {
      const response = await apiClient.get<Record<string, number>>(`${basePath}/status-counts`)
      return response.data
    },
  })

  // Purgeable counts for the matched/partial_match buckets (and their sum under "all").
  // Purgeable can't be a live counter — a document ages into it as dates pass, with no
  // transition to track — so the server counts on demand. Putting the matched and
  // partial_match tab counts in the key (with staleTime: Infinity) recomputes it only when
  // those buckets move, so it runs once on load and refreshes as documents flow through.
  const matchedCount = tabCounts?.matched ?? null
  const partialCount = tabCounts?.partial_match ?? null
  const { data: purgeableCounts } = useQuery({
    queryKey: ['i9', 'purgeable-counts', orgId, workspaceId, matchedCount, partialCount],
    enabled: matchedCount !== null && partialCount !== null,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<Record<string, number>> => {
      const response = await apiClient.get<Record<string, number>>(`${basePath}/purgeable-counts`)
      return response.data
    },
  })
  // Only matched/partial_match/all carry a purgeable count; null on the other tabs.
  const purgeableCount = purgeableCounts?.[activeTab] ?? null

  // Poll rerun records still in flight
  const rerunList = Array.from(rerunIds).sort()
  const activePollIds = rerunList.filter((id) => {
    const s = rerunStatus[id]?.status
    return !s || !TERMINAL_STATUSES.includes(s)
  })

  const { data: polled } = useQuery({
    queryKey: ['i9', 'rerun-poll', orgId, workspaceId, activePollIds.join(',')],
    enabled: activePollIds.length > 0,
    refetchInterval: 3000,
    queryFn: async (): Promise<OptixDocument[]> => {
      const response = await apiClient.post<{ documents: OptixDocument[] }>(
        `${basePath}/documents/batch`,
        { objectIds: activePollIds }
      )
      return response.data.documents
    },
  })

  useEffect(() => {
    if (!polled) return
    let resolvedAny = false
    setRerunStatus((prev) => {
      const next = { ...prev }
      for (const doc of polled) {
        if (next[doc.docId]?.status !== doc.status && TERMINAL_STATUSES.includes(doc.status)) {
          resolvedAny = true
        }
        next[doc.docId] = doc
      }
      return next
    })
    if (resolvedAny) {
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents'] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'status-counts', orgId, workspaceId] })
    }
  }, [polled, queryClient, orgId, workspaceId])

  const retryMutation = useMutation({
    // One request re-queues every selected id. crop === null means "no override" ->
    // omit the fractions so the server keeps the host-configured band; only an
    // explicit band is forwarded. The server skips ids that are missing or in flight.
    mutationFn: ({ ids, crop }: { ids: string[]; crop: CropValues | null }) =>
      apiClient.post<{ queued: string[]; skipped: string[] }>(
        `${basePath}/documents/batch-retry`,
        { objectIds: ids, ...(crop ?? {}) }
      ),
    onSuccess: ({ data }) => {
      const { queued } = data
      setRerunIds((prev) => new Set([...prev, ...queued]))
      setRerunStatus((prev) => {
        const next = { ...prev }
        for (const docId of queued) {
          next[docId] = { ...next[docId], docId, status: 'awaiting_retry', empName: null, scanDate: null }
        }
        return next
      })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'status-counts', orgId, workspaceId] })
    },
  })

  const retryAllMutation = useMutation({
    // Re-queue every eligible document in the active tab (all pages), optionally
    // skipping documents whose reviewer fail type is excluded. The server just accepts
    // the request (returns a jobId) and the i9processor fans the tab out asynchronously,
    // so no per-document ids come back.
    mutationFn: ({ tab, excludeFailTypes, crop, skipSameBand }: { tab: TabValue; excludeFailTypes: FailType[]; crop: CropValues | null; skipSameBand: boolean }) =>
      apiClient.post<{ status: string; jobId: string }>(
        `${basePath}/documents/retry-all`,
        { tab, excludeFailTypes, skipSameBand, ...(crop ?? {}) }
      ),
    // Re-run all can queue thousands of documents, so they are NOT added to the rerun
    // tracker (no per-doc polling). The i9processor moves them into the Waiting tab as it
    // stages each one; the tab's periodic count refresh reflects them as they land.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'status-counts', orgId, workspaceId] })
    },
  })

  const commentMutation = useMutation({
    // Set/clear a single document's comment. Status is unchanged, so tab counts don't
    // move; invalidate the row list and any active search (whose row would otherwise
    // keep the old comment).
    mutationFn: ({ objectId, comment }: { objectId: string; comment: string | null }) =>
      apiClient.post<OptixDocument>(`${basePath}/documents/${objectId}/comment`, { comment }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'document-search', orgId, workspaceId] })
    },
  })

  const failTypeMutation = useMutation({
    // Set/clear a reviewer's failure category. Like comment, status is unchanged, so
    // tab counts don't move; invalidate the row list and any active search (whose row
    // would otherwise keep the old fail type).
    mutationFn: ({ objectId, failType }: { objectId: string; failType: FailType | null }) =>
      apiClient.post<OptixDocument>(`${basePath}/documents/${objectId}/fail-type`, { failType }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'document-search', orgId, workspaceId] })
    },
  })

  // Manual-disposition actions (shred/unshred/purge) all POST the same shape and change
  // the document's status, so each invalidates the row lists, the tab counts, and any
  // active doc-number/object-id search (whose row would otherwise keep the old status);
  // only the action segment of the path differs. A rejection (e.g. the 400 for a
  // non-shreddable status) is surfaced as a toast — these buttons have no other error UI.
  const dispositionMutationOptions = (action: 'shred' | 'unshred' | 'purge') => ({
    mutationFn: (objectId: string) =>
      apiClient.post<OptixDocument>(`${basePath}/documents/${objectId}/${action}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'status-counts', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'document-search', orgId, workspaceId] })
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } } }
      const description = axiosError.response?.data?.detail
        ?? (error instanceof Error ? error.message : undefined)
      toast.error(`Failed to ${action} document`, { description })
    },
  })
  const shredMutation = useMutation(dispositionMutationOptions('shred'))
  const unshredMutation = useMutation(dispositionMutationOptions('unshred'))
  const purgeMutation = useMutation(dispositionMutationOptions('purge'))

  // Force shred (global admin only): same endpoint as the normal shred, but force=true
  // tells the server to bypass the terminal-status and retention-window guards. Shares
  // the disposition invalidation/error shape; only the query string differs.
  const forceShredMutation = useMutation({
    ...dispositionMutationOptions('shred'),
    mutationFn: (objectId: string) =>
      apiClient.post<OptixDocument>(`${basePath}/documents/${objectId}/shred?force=true`, {}),
  })

  // Rematch (re-score stored OCR under current rules) and Optix-rewrite (re-push the
  // promoted run's metadata) are global-admin jobs that queue asynchronously — the
  // response is a jobId, not the updated record — so success just reports the queue and
  // lets the tab's periodic count refresh reflect the outcome. Only the action segment
  // and label differ; errors surface as a toast (these buttons have no other error UI).
  const queuedJobMutationOptions = (action: 'rematch' | 'rewrite-optix', label: string) => ({
    mutationFn: (objectId: string) =>
      apiClient.post<{ status: string; jobId: string }>(
        `${basePath}/documents/${objectId}/${action}`, {}
      ),
    onSuccess: () => {
      toast.success(`${label} queued`)
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'status-counts', orgId, workspaceId] })
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } } }
      const description = axiosError.response?.data?.detail
        ?? (error instanceof Error ? error.message : undefined)
      toast.error(`Failed to queue ${label.toLowerCase()}`, { description })
    },
  })
  const rematchMutation = useMutation(queuedJobMutationOptions('rematch', 'Rematch'))
  const rewriteOptixMutation = useMutation(queuedJobMutationOptions('rewrite-optix', 'Optix rewrite'))

  const rematchAllMutation = useMutation({
    // Rematch every eligible document in the Partial Match tab (all pages). The server
    // enumerates the tab and fans out one processor job per document, returning the
    // queued/eligible counts; the tab's periodic count refresh reflects re-bucketed
    // documents as they land. Restricted server-side to the partial_match tab.
    mutationFn: (tab: TabValue) =>
      apiClient.post<{ status: string; queued: number; eligible: number }>(
        `${basePath}/documents/rematch-all`, { tab }
      ),
    onSuccess: ({ data }) => {
      toast.success(
        `Rematch queued for ${data.queued.toLocaleString()} document${data.queued === 1 ? '' : 's'}`
      )
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'status-counts', orgId, workspaceId] })
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } } }
      const description = axiosError.response?.data?.detail
        ?? (error instanceof Error ? error.message : undefined)
      toast.error('Failed to queue rematch all', { description })
    },
  })

  const rewriteOptixAllMutation = useMutation({
    // Re-push Optix metadata for every document whose last write-back failed (all tabs).
    // The server enumerates the failed-write set and fans out one processor job per
    // document, returning the queued/eligible counts; the tab's periodic count refresh
    // reflects any re-bucketing as they land. No tab or options — the failed set is
    // defined entirely by the stored write status.
    mutationFn: () =>
      apiClient.post<{ status: string; queued: number; eligible: number }>(
        `${basePath}/documents/rewrite-optix-all`, {}
      ),
    onSuccess: ({ data }) => {
      toast.success(
        `Optix rewrite queued for ${data.queued.toLocaleString()} document${data.queued === 1 ? '' : 's'}`
      )
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'status-counts', orgId, workspaceId] })
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } } }
      const description = axiosError.response?.data?.detail
        ?? (error instanceof Error ? error.message : undefined)
      toast.error('Failed to queue Optix rewrite all', { description })
    },
  })

  const rewriteOptixBatchMutation = useMutation({
    // Re-push Optix metadata for an explicit list of pasted object ids (all tabs). The
    // server publishes one processor job per eligible id and returns the queued/skipped
    // ids; ids that don't exist or are in flight are skipped rather than failing the
    // batch. The queued ids feed the rerun tracker so their outcome can be followed.
    mutationFn: (objectIds: string[]) =>
      apiClient.post<{ queued: string[]; skipped: string[] }>(
        `${basePath}/documents/batch-rewrite-optix`,
        { objectIds }
      ),
    onSuccess: ({ data }) => {
      const { queued, skipped } = data
      toast.success(
        `Optix rewrite queued for ${queued.length.toLocaleString()} document${queued.length === 1 ? '' : 's'}` +
          (skipped.length > 0 ? ` · ${skipped.length.toLocaleString()} skipped` : '')
      )
      setRerunIds((prev) => new Set([...prev, ...queued]))
      setRerunStatus((prev) => {
        const next = { ...prev }
        for (const docId of queued) {
          next[docId] = { ...next[docId], docId, status: 'awaiting_retry', empName: null, scanDate: null }
        }
        return next
      })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'optix-documents', orgId, workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['i9', 'status-counts', orgId, workspaceId] })
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } } }
      const description = axiosError.response?.data?.detail
        ?? (error instanceof Error ? error.message : undefined)
      toast.error('Failed to queue Optix rewrite', { description })
    },
  })

  // Only records that aren't already in flight (or manually dispositioned) can be retried.
  const eligibleIds = documents
    .filter((d) => !IN_FLIGHT_STATUSES.includes(d.status) && !MANUAL_STATUSES.includes(d.status))
    .map((d) => d.docId)
  const allEligibleSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedIds.has(id))

  const toggleSelected = (docId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(docId)) next.delete(docId)
      else next.add(docId)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelectedIds(allEligibleSelected ? new Set() : new Set(eligibleIds))
  }

  const handleRetryConfirm = async (crop: CropValues | null) => {
    await retryMutation.mutateAsync({ ids: Array.from(selectedIds), crop })
    setSelectedIds(new Set())
    setRetryDialogOpen(false)
  }

  const handleRetryAllConfirm = async (
    excludeFailTypes: FailType[],
    crop: CropValues | null,
    skipSameBand: boolean,
  ) => {
    await retryAllMutation.mutateAsync({ tab: activeTab, excludeFailTypes, crop, skipSameBand })
    setRetryAllDialogOpen(false)
  }

  const handleRematchAllConfirm = async () => {
    await rematchAllMutation.mutateAsync(activeTab)
    setRematchAllDialogOpen(false)
  }

  const handleRewriteOptixAllConfirm = async () => {
    await rewriteOptixAllMutation.mutateAsync()
    setRewriteOptixAllDialogOpen(false)
  }

  const handleRewriteOptixBatchConfirm = async (objectIds: string[]) => {
    await rewriteOptixBatchMutation.mutateAsync(objectIds)
    setRewriteOptixBatchDialogOpen(false)
  }

  const handleCommentConfirm = async (comment: string | null) => {
    if (!commentTarget) return
    await commentMutation.mutateAsync({ objectId: commentTarget.docId, comment })
    setCommentTarget(null)
  }

  const handlePurgeConfirm = async () => {
    if (!purgeTarget) return
    try {
      await purgeMutation.mutateAsync(purgeTarget.docId)
    } catch {
      // Surfaced by the mutation's onError toast; still close the dialog.
    }
    setPurgeTarget(null)
  }

  const handleForceShredConfirm = async () => {
    if (!forceShredTarget) return
    try {
      await forceShredMutation.mutateAsync(forceShredTarget.docId)
    } catch {
      // Surfaced by the mutation's onError toast; still close the dialog.
    }
    setForceShredTarget(null)
  }

  // Total pages come from the per-tab count (status-counts); the current page's next
  // cursor tells us a further page exists even before it's been visited.
  const tabCount = tabCounts?.[activeTab] ?? null
  const totalPages = tabCount !== null ? Math.max(1, Math.ceil(tabCount / PAGE_SIZE)) : null
  const hasNextPage = cursors[page + 1] !== undefined || Boolean(data?.nextCursor)

  // The rows and the tab counts are independent queries that drift out of sync: the counts
  // poll every 10s, but a row page only refetches on demand, so records that changed status
  // in the backend can linger as stale rows (Waiting shows 4 when there are 0) or be missing
  // (an under-filled page). When the rendered row count doesn't match what the count implies
  // for this page, we can't tell which side is stale, so refetch BOTH and let them converge.
  // The snapshot key makes this self-limiting: once a refetch returns the same numbers the
  // key is unchanged and we stop; any real movement (rows change, count polls) re-arms a
  // single reconcile.
  const lastReconcileKey = useRef<string | null>(null)
  useEffect(() => {
    if (isSearching || isLoading || isFetching || !data || tabCount === null) return
    // Counts are unfiltered, so they can't predict a filtered page's row count. When
    // filtering, freshness comes from the poll instead.
    if (isFiltered) return
    const expectedRows = Math.max(0, Math.min(PAGE_SIZE, tabCount - page * PAGE_SIZE))
    if (documents.length === expectedRows) return
    const key = `${activeTab}:${page}:${cursors[page]}:${documents.length}:${tabCount}`
    if (lastReconcileKey.current === key) return
    lastReconcileKey.current = key
    void refetch()
    void refetchCounts()
  }, [data, documents.length, tabCount, isLoading, isFetching, activeTab, page, cursors, isFiltered, isSearching, refetch, refetchCounts])

  // Numbered buttons for a small window of already-visited pages around the current one.
  const windowStart = Math.max(0, page - 2)
  const windowEnd = Math.min(cursors.length - 1, page + 2)
  const pageNumbers = Array.from({ length: Math.max(0, windowEnd - windowStart + 1) }, (_, i) => windowStart + i)

  const goToPage = (target: number) => {
    if (target < 0 || cursors[target] === undefined) return
    setPage(target)
  }

  const handleTabChange = (value: TabValue) => {
    setSelectedTab(value)
    setPage(0)
    setCursors([''])
    setSelectedIds(new Set())
  }

  // Toggling a filter or the sort changes the result set/order, so restart paging
  // from the first page (cursors from the old ordering are meaningless).
  const resetPaging = () => {
    setPage(0)
    setCursors([''])
    setSelectedIds(new Set())
  }

  const handleUnreviewedToggle = (checked: boolean) => {
    setUnreviewedOnly(checked)
    resetPaging()
  }

  const handlePurgeableToggle = (checked: boolean) => {
    setPurgeableOnly(checked)
    resetPaging()
  }

  const handleSortToggle = () => {
    setSortAsc((prev) => !prev)
    resetPaging()
  }

  const handleSearchSubmit = () => {
    setSearchId(searchInput.trim() || null)
    setSelectedIds(new Set())
  }

  const clearSearch = () => {
    setSearchInput('')
    setSearchId(null)
    setSelectedIds(new Set())
  }

  // The error and match-review tabs are the terminal, reviewable outcomes: a reviewer
  // can categorize why they failed (fail type) and re-run them all.
  const canRetryAll = showFailType && !isSearching

  const colSpan = 9 + (isGlobalAdmin ? 1 : 0) + (canReview ? 1 : 0) + (showFailType ? 1 : 0)

  return (
    <div className="space-y-6 p-6" data-testid="i9-dashboard">
      {/* Workflow Overview */}
      <Card data-testid="i9-dashboard">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5" />
            I-9 Compliance Verification
          </CardTitle>
          <CardDescription>
            Verify I-9 forms against UMD employee records
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <FileStack className="h-4 w-4 text-blue-500" />
              <span>Fetch</span>
            </div>
            <span>→</span>
            <div className="flex items-center gap-1">
              <Eye className="h-4 w-4 text-purple-500" />
              <span>Extract</span>
            </div>
            <span>→</span>
            <div className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Match</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-lg">Optix Documents</CardTitle>
            <div className="flex flex-wrap gap-2">
              {isGlobalAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRetryDialogOpen(true)}
                  disabled={selectedIds.size === 0 || retryMutation.isPending}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Retry selected ({selectedIds.size})
                </Button>
              )}
              {isGlobalAdmin && canRetryAll && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRetryAllDialogOpen(true)}
                  disabled={retryAllMutation.isPending || (tabCount ?? 0) === 0}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Re-run all
                </Button>
              )}
              {isGlobalAdmin && activeTab === 'partial_match' && !isSearching && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRematchAllDialogOpen(true)}
                  disabled={rematchAllMutation.isPending || (tabCount ?? 0) === 0}
                >
                  <Calculator className="h-4 w-4 mr-1" />
                  Rematch all
                </Button>
              )}
              {isGlobalAdmin && !isSearching && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRewriteOptixAllDialogOpen(true)}
                  disabled={rewriteOptixAllMutation.isPending}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Re-run Optix failures
                </Button>
              )}
              {isGlobalAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRewriteOptixBatchDialogOpen(true)}
                  disabled={rewriteOptixBatchMutation.isPending}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Retry Optix by ID
                </Button>
              )}
              <DocsDialog />
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form
            className="mb-4 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              handleSearchSubmit()
            }}
          >
            <Select value={searchBy} onValueChange={(v) => setSearchBy(v as 'doc_number' | 'object_id')}>
              <SelectTrigger className="w-[130px]" aria-label="Search by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="doc_number">Doc #</SelectItem>
                <SelectItem value="object_id">Object ID</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={searchBy === 'doc_number' ? 'Search by Doc #' : 'Search by Object ID'}
                className="pl-8"
                aria-label={searchBy === 'doc_number' ? 'Search by Doc #' : 'Search by Object ID'}
              />
            </div>
            <Button type="submit" variant="outline" size="sm" disabled={!searchInput.trim()}>
              Search
            </Button>
            {isSearching && (
              <Button type="button" variant="ghost" size="sm" onClick={clearSearch}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </form>

          <Tabs value={activeTab} onValueChange={(v) => handleTabChange(v as TabValue)}>
            <TabsList className="mb-4 flex flex-wrap h-auto">
              {visibleTabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value} className="gap-1.5">
                  {t.label}
                  {tabCounts && ` (${(tabCounts[t.value] ?? 0).toLocaleString()})`}
                  {isMatchRuleTab(t.value) && <MatchRulesInfo bucket={t.value} />}
                </TabsTrigger>
              ))}
            </TabsList>

            {!isSearching && (showFailType || tabShowPurgeable) && (
              <div className="mb-4 flex flex-wrap gap-4">
                {showFailType && (
                  <label className="flex w-fit cursor-pointer select-none items-center gap-2 text-sm">
                    <Checkbox
                      checked={unreviewedOnly}
                      onCheckedChange={(checked) => handleUnreviewedToggle(checked === true)}
                    />
                    Unreviewed only
                  </label>
                )}
                {tabShowPurgeable && (
                  <label className="flex w-fit cursor-pointer select-none items-center gap-2 text-sm">
                    <Checkbox
                      checked={purgeableOnly}
                      onCheckedChange={(checked) => handlePurgeableToggle(checked === true)}
                    />
                    Purgeable only
                    {purgeableCount !== null && ` (${purgeableCount.toLocaleString()})`}
                  </label>
                )}
              </div>
            )}

            {fetchError && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-4 text-sm text-red-700">
                Error loading documents: {fetchError.message}
              </div>
            )}

            <Table data-testid="i9-documents-table">
              <TableHeader>
                <TableRow>
                  {isGlobalAdmin && (
                    <TableHead className="w-8">
                      <Checkbox
                        checked={allEligibleSelected}
                        onCheckedChange={toggleSelectAll}
                        disabled={eligibleIds.length === 0}
                        aria-label="Select all"
                      />
                    </TableHead>
                  )}
                  <TableHead>
                    <button
                      type="button"
                      className="flex items-center gap-1 hover:text-foreground"
                      onClick={handleSortToggle}
                      aria-label={`Sort by doc number, ${sortAsc ? 'descending' : 'ascending'}`}
                    >
                      Doc #
                      {sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                    </button>
                  </TableHead>
                  <TableHead>Object ID</TableHead>
                  <TableHead>Employee Name</TableHead>
                  <TableHead>UID</TableHead>
                  <TableHead>Scan Date</TableHead>
                  <TableHead>Employment Status</TableHead>
                  <TableHead>Status</TableHead>
                  {showFailType && <TableHead>Fail Type</TableHead>}
                  <TableHead>Crop</TableHead>
                  <TableHead>Processed</TableHead>
                  {canReview && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isSearching && searchFetching && (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="text-center text-muted-foreground">
                      <Loader2 className="mr-1 inline h-4 w-4 animate-spin" />
                      Searching…
                    </TableCell>
                  </TableRow>
                )}
                {documents.length === 0 && !isLoading && !(isSearching && searchFetching) && (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="text-center text-muted-foreground">
                      {isSearching ? `No document found for “${searchId}”` : 'No documents found'}
                    </TableCell>
                  </TableRow>
                )}
                {documents.map((doc) => (
                  <TableRow key={doc.docId}>
                    {isGlobalAdmin && (
                      <TableCell>
                        {!IN_FLIGHT_STATUSES.includes(doc.status) && !MANUAL_STATUSES.includes(doc.status) && (
                          <Checkbox
                            checked={selectedIds.has(doc.docId)}
                            onCheckedChange={() => toggleSelected(doc.docId)}
                            aria-label={`Select ${doc.docId}`}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="font-mono">{doc.docNumber || '—'}</TableCell>
                    <TableCell className="font-mono text-muted-foreground">{doc.docId}</TableCell>
                    <TableCell>
                      <EmployeeNameBadge match={doc.matchResult ?? null} optixName={doc.empName ?? null} />
                    </TableCell>
                    <TableCell className="font-mono text-muted-foreground">
                      {doc.matchResult?.umdData?.uid ?? '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{doc.scanDate?.slice(0, 10) ?? '—'}</TableCell>
                    <TableCell>
                      <span data-testid="i9-match-badges"><PurgeableBadge match={doc.matchResult ?? null} /></span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <StatusBadge status={doc.status} />
                        <OperationalWarning
                          operationalError={doc.operationalError}
                          optixWriteStatus={doc.optixWriteStatus}
                          optixDeleteStatus={doc.optixDeleteStatus}
                          optixDeleteError={doc.optixDeleteError}
                        />
                      </div>
                    </TableCell>
                    {showFailType && (
                      <TableCell>
                        {canReview ? (
                          <FailTypeSelect
                            value={doc.failType ?? null}
                            disabled={failTypeMutation.isPending}
                            onChange={(failType) => failTypeMutation.mutate({ objectId: doc.docId, failType })}
                          />
                        ) : (
                          <span className="text-muted-foreground">{doc.failType ?? '—'}</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatCrop(doc.cropTopFraction, doc.cropBottomFraction)}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(doc.processedAt)}
                    </TableCell>
                    {canReview && (
                      <TableCell>
                        <div className="flex items-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCommentTarget(doc)}
                            aria-label={`Comment on ${doc.docId}`}
                            title={doc.comment || 'Add or edit comment'}
                          >
                            <MessageSquare className={`h-4 w-4 ${doc.comment ? 'text-blue-600' : ''}`} />
                          </Button>
                          {doc.history && doc.history.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setHistoryTarget(doc)}
                              aria-label={`Run history for ${doc.docId}`}
                              title={`Run history (${doc.history.length})`}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          )}
                          {isGlobalAdmin && TERMINAL_STATUSES.includes(doc.status) && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => rematchMutation.mutate(doc.docId)}
                                disabled={rematchMutation.isPending}
                                aria-label={`Recalculate match for ${doc.docId}`}
                                title="Recalculate match (re-score stored OCR under current rules — no re-OCR)"
                              >
                                <Calculator className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => rewriteOptixMutation.mutate(doc.docId)}
                                disabled={rewriteOptixMutation.isPending}
                                aria-label={`Retry Optix metadata update for ${doc.docId}`}
                                title="Retry Optix metadata update (re-push the current result to Optix — no re-OCR or re-match)"
                              >
                                <Upload className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {!isGlobalAdmin && TERMINAL_STATUSES.includes(doc.status) && isPurgeable(doc) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => shredMutation.mutate(doc.docId)}
                              disabled={shredMutation.isPending}
                              aria-label={`Shred ${doc.docId}`}
                              title="Move to Shredded (paper document has been shredded)"
                            >
                              <Scissors className="h-4 w-4" />
                            </Button>
                          )}
                          {isGlobalAdmin && !MANUAL_STATUSES.includes(doc.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setForceShredTarget(doc)}
                              aria-label={`Shred ${doc.docId}`}
                              title="Move to Shredded from any bucket (bypasses the retention and status checks — global admin)"
                            >
                              <Scissors className="h-4 w-4" />
                            </Button>
                          )}
                          {doc.status === 'shredded' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => unshredMutation.mutate(doc.docId)}
                              disabled={unshredMutation.isPending}
                              aria-label={`Unshred ${doc.docId}`}
                              title="Undo shred (restore to its processing status)"
                            >
                              <Undo2 className="h-4 w-4" />
                            </Button>
                          )}
                          {(doc.status === 'shredded' ||
                            (doc.status === 'purged' && doc.optixDeleteStatus === 'failed')) &&
                            doc.optixDeleteStatus !== 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPurgeTarget(doc)}
                              aria-label={`Purge ${doc.docId}`}
                              title={
                                doc.status === 'purged'
                                  ? 'Retry the failed Optix delete'
                                  : 'Purge document (deletes it from Optix)'
                              }
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {isSearching ? (
              <div className="mt-4 text-xs text-muted-foreground">
                Showing search result for “{searchId}”
              </div>
            ) : (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {isFiltered
                  ? `${documents.length.toLocaleString()} matching on this page`
                  : tabCount !== null
                    ? `${tabCount.toLocaleString()} document${tabCount === 1 ? '' : 's'}`
                    : `${documents.length} loaded`}
                {optixTotalCount !== null && ` · ${optixTotalCount.toLocaleString()} total in Optix`}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 0 || isFetching}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {pageNumbers.map((p) => (
                  <Button
                    key={p}
                    variant={p === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => goToPage(p)}
                    disabled={isFetching}
                  >
                    {p + 1}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(page + 1)}
                  disabled={!hasNextPage || isFetching}
                  aria-label="Next page"
                >
                  {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
                <span className="ml-2 text-xs text-muted-foreground whitespace-nowrap">
                  Page {(page + 1).toLocaleString()}
                  {!isFiltered && totalPages !== null && ` of ${totalPages.toLocaleString()}`}
                </span>
              </div>
            </div>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Rerun tracker */}
      {rerunList.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <RotateCcw className="h-5 w-5 text-orange-500" />
              Reprocessing ({rerunList.length})
            </CardTitle>
            <CardDescription>Documents rerun from this dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Object ID</TableHead>
                  <TableHead>Optix Doc #</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Employment Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rerunList.map((docId) => {
                  const doc = rerunStatus[docId]
                  const status = doc?.status ?? 'awaiting_retry'
                  const inFlight = !TERMINAL_STATUSES.includes(status)
                  return (
                    <TableRow key={docId}>
                      <TableCell className="font-mono">{docId}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">{doc?.docNumber || '—'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs inline-flex items-center gap-1 ${statusColor(status)}`}>
                          {inFlight && <Loader2 className="h-3 w-3 animate-spin" />}
                          {status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <PurgeableBadge match={doc?.matchResult ?? null} />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* HR match dry-run — global admin only */}
      {isGlobalAdmin && <MatchDebugCard orgId={orgId} workspaceId={workspaceId} />}

      {/* Optix schema debug — global admin only */}
      {isGlobalAdmin && <OptixDebugCard orgId={orgId} workspaceId={workspaceId} />}

      <RetryDialog
        open={retryDialogOpen}
        onOpenChange={setRetryDialogOpen}
        selectedCount={selectedIds.size}
        isRetrying={retryMutation.isPending}
        onConfirm={handleRetryConfirm}
      />

      <RetryAllDialog
        open={retryAllDialogOpen}
        onOpenChange={setRetryAllDialogOpen}
        tabLabel={TABS.find((t) => t.value === activeTab)?.label ?? activeTab}
        documentCount={tabCount}
        showExclusions={showFailType}
        isRetrying={retryAllMutation.isPending}
        onConfirm={handleRetryAllConfirm}
      />

      <RematchAllDialog
        open={rematchAllDialogOpen}
        onOpenChange={setRematchAllDialogOpen}
        documentCount={tabCount}
        isRematching={rematchAllMutation.isPending}
        onConfirm={handleRematchAllConfirm}
      />

      <RewriteOptixAllDialog
        open={rewriteOptixAllDialogOpen}
        onOpenChange={setRewriteOptixAllDialogOpen}
        isRewriting={rewriteOptixAllMutation.isPending}
        onConfirm={handleRewriteOptixAllConfirm}
      />

      <RewriteOptixBatchDialog
        open={rewriteOptixBatchDialogOpen}
        onOpenChange={setRewriteOptixBatchDialogOpen}
        isRewriting={rewriteOptixBatchMutation.isPending}
        onConfirm={handleRewriteOptixBatchConfirm}
      />

      <CommentDialog
        open={commentTarget !== null}
        onOpenChange={(open) => !open && setCommentTarget(null)}
        docId={commentTarget?.docId ?? null}
        initialComment={commentTarget?.comment ?? null}
        isSaving={commentMutation.isPending}
        onConfirm={handleCommentConfirm}
      />

      <HistoryDialog
        open={historyTarget !== null}
        onOpenChange={(open) => !open && setHistoryTarget(null)}
        docId={historyTarget?.docId ?? null}
        history={historyTarget?.history ?? []}
      />

      <PurgeDialog
        open={purgeTarget !== null}
        onOpenChange={(open) => !open && setPurgeTarget(null)}
        docId={purgeTarget?.docId ?? null}
        isPurging={purgeMutation.isPending}
        onConfirm={handlePurgeConfirm}
      />

      <ShredDialog
        open={forceShredTarget !== null}
        onOpenChange={(open) => !open && setForceShredTarget(null)}
        docId={forceShredTarget?.docId ?? null}
        status={forceShredTarget?.status ?? null}
        isShredding={forceShredMutation.isPending}
        onConfirm={handleForceShredConfirm}
      />
    </div>
  )
}
